// @flow
import BaseCollection from "../../base/BaseCollection";

import BaseLayoutHintRule from "./BaseLayoutHintRule";

import DependentAttribute from "./DependentAttribute";
import RemainingTotalUploadSize from "./RemainingTotalUploadSize";

import { IllegalReturnException } from "../../../exceptions";

import type AttributeCollection from "../AttributeCollection";
import type { AttributeType, ILayoutHintRule } from "../../types";
import type AttributeModel from "../AttributeModel";

/**
 * Array of available layout hint rules
 * @private
 */
let layoutHintRules: Array<Class<BaseLayoutHintRule>> = [
  DependentAttribute,
  RemainingTotalUploadSize,
];

/**
 * Add custom layout hint rules
 */
export const addLayoutHintRule = (rule: Class<BaseLayoutHintRule>) => {
  layoutHintRules.push(rule);
};

type LayoutHintRulesUpdater = (
  rules: Array<Class<BaseLayoutHintRule>>,
) => Array<Class<BaseLayoutHintRule>>;

/**
 * Update existing array of layout hint rules, use this to update or remove standard rules
 *
 * @example
 * // remove RemainingTotalUploadSize rule
 * updateLayoutHintRules((rules) => {
 *   return rules.filter(rule => rule !== RemainingTotalUploadSize));
 * });
 */
export const updateLayoutHintRules = (updater: LayoutHintRulesUpdater) => {
  const newRules = updater(layoutHintRules);
  if (Array.isArray(newRules)) {
    layoutHintRules = newRules;
  } else {
    throw new IllegalReturnException(
      "Result of updater of updateLayoutHintRules is not an array",
    );
  }
};

/**
 * Collection of layout hint rules
 */
class LayoutHintRuleCollection extends BaseCollection<ILayoutHintRule> {
  /**
   * Retrieve all rules available in the collection
   */
  get allRules(): Array<Class<BaseLayoutHintRule>> {
    return layoutHintRules;
  }

  /**
   * Set layout hint rules on the applicable attributes
   */
  static setLayoutHintRules<T: AttributeType>(attribute: T): T {
    const rules = new LayoutHintRuleCollection();

    layoutHintRules.forEach((rule) => {
      if (rule.isApplicableForAttribute(attribute)) {
        if (rule.checkHintApplicability) {
          attribute.layouthint.forEach((hint) => {
            if (rule.isApplicableForHint(hint)) {
              rules.add(rule.initHint(hint));
            }
          });
        } else {
          rules.add(rule.initAttribute(attribute));
        }
      }
    });

    attribute.layoutHintRules = rules;

    return attribute;
  }

  /**
   * Process layout hint rules in the collection
   */
  process(attribute: AttributeModel, attributes: AttributeCollection) {
    this.all.forEach((rule) => {
      rule.process(attribute, attributes);
    });
  }
}

export default LayoutHintRuleCollection;
